import React, { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Send, MessageSquare, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatName } from "@/lib/format";
import { toast } from "sonner";

function initials(name) {
  return (name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");
}

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  const diffDays = Math.floor((now - d) / 86400000);
  if (diffDays < 7) {
    return d.toLocaleDateString([], { weekday: "short" }) + " " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString();
}

function roleColor(role) {
  const r = { admin: "master", consultant: "basic" }[role] || role;
  return {
    master: "bg-prime",
    control: "bg-amber-600",
    basic: "bg-slate-600",
    finanza: "bg-emerald-700",
  }[r] || "bg-slate-500";
}

export default function ChatPage() {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [q, setQ] = useState("");
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const activeIdRef = useRef(null);

  useEffect(() => {
    activeIdRef.current = activeId;
  }, [activeId]);

  const fetchContacts = useCallback(async () => {
    try {
      const r = await api.get("/chat/contacts");
      setContacts(r.data || []);
      setLoadingContacts(false);
      if (!activeIdRef.current && r.data?.length) {
        setActiveId(r.data[0].id);
      }
    } catch {
      setLoadingContacts(false);
    }
  }, []);

  const fetchMessages = useCallback(async (peerId) => {
    if (!peerId) return;
    try {
      const r = await api.get(`/chat/conversations/${peerId}`);
      setMessages(r.data || []);
      // mark read
      await api.post(`/chat/conversations/${peerId}/read`).catch(() => {});
      // refresh contacts to reflect read state
      fetchContacts();
    } catch {
      /* */
    }
  }, [fetchContacts]);

  // initial + polling for contacts (unread counts)
  useEffect(() => {
    fetchContacts();
    const t = setInterval(fetchContacts, 8000);
    return () => clearInterval(t);
  }, [fetchContacts]);

  // load + poll active conversation
  useEffect(() => {
    if (!activeId) return;
    fetchMessages(activeId);
    const t = setInterval(() => fetchMessages(activeIdRef.current), 5000);
    return () => clearInterval(t);
  }, [activeId, fetchMessages]);

  // scroll to bottom on new messages
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, activeId]);

  const send = async () => {
    const body = draft.trim();
    if (!body || !activeId || sending) return;
    setSending(true);
    try {
      const r = await api.post("/chat/messages", { to_id: activeId, body });
      setMessages((m) => [...m, r.data]);
      setDraft("");
      fetchContacts();
    } catch (e) {
      toast.error("Fehler invio messaggio");
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const filtered = contacts.filter((c) =>
    !q.trim() ? true : (c.full_name || "").toLowerCase().includes(q.trim().toLowerCase())
  );

  const active = contacts.find((c) => c.id === activeId);

  return (
    <div className="flex flex-col h-[calc(100vh-6rem)]" data-testid="chat-page">
      <div className="mb-4">
        <div className="label-uppercase text-prime">/ Chat</div>
        <h1 className="font-display font-extrabold text-4xl lg:text-5xl tracking-tight mt-1">Team Chat</h1>
      </div>

      <div className="prime-card flex-1 grid grid-cols-1 md:grid-cols-[300px_1fr] overflow-hidden">
        {/* Contacts */}
        <aside className="border-r border-border flex flex-col min-h-0">
          <div className="px-4 py-3 border-b border-border">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Kontakt suchen…"
                className="pl-9 h-9 text-sm rounded-md"
                data-testid="chat-search"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto" data-testid="chat-contacts-list">
            {loadingContacts ? (
              <div className="p-6 text-center text-sm text-muted-foreground">Lädt…</div>
            ) : filtered.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                {contacts.length === 0 ? "Kein membro del team disponibile." : "Keine Ergebnisse."}
              </div>
            ) : (
              filtered.map((c) => {
                const isActive = c.id === activeId;
                return (
                  <button
                    key={c.id}
                    onClick={() => setActiveId(c.id)}
                    data-testid={`chat-contact-${c.id}`}
                    className={`w-full text-left flex items-center gap-3 px-4 py-3 border-b border-border/60 transition-colors ${
                      isActive ? "bg-secondary" : "hover:bg-secondary/60"
                    }`}
                  >
                    <div className={`w-9 h-9 shrink-0 rounded-full ${roleColor(c.role)} text-white flex items-center justify-center text-xs font-display font-bold`}>
                      {initials(c.full_name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="font-medium text-sm truncate flex-1">{formatName(c.full_name)}</div>
                        {c.last_at && (
                          <div className="text-[10px] text-muted-foreground shrink-0 font-mono">{fmtTime(c.last_at)}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <div className="text-xs text-muted-foreground truncate flex-1">
                          {c.last_body || <span className="italic opacity-60">Kein messaggio</span>}
                        </div>
                        {c.unread > 0 && (
                          <span
                            className="shrink-0 inline-flex items-center justify-center text-[10px] font-bold text-white bg-prime rounded-full min-w-[18px] h-[18px] px-1"
                            data-testid={`chat-unread-${c.id}`}
                          >
                            {c.unread}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </aside>

        {/* Thread */}
        <section className="flex flex-col min-h-0">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <MessageSquare className="w-10 h-10 mx-auto opacity-30 mb-3" strokeWidth={1.5} />
                <div className="text-sm">Auswählen un contatto per iniziare</div>
              </div>
            </div>
          ) : (
            <>
              <header className="px-6 py-4 border-b border-border flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full ${roleColor(active.role)} text-white flex items-center justify-center text-sm font-display font-bold`}>
                  {initials(active.full_name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-lg tracking-tight truncate" data-testid="chat-thread-name">
                    {formatName(active.full_name)}
                  </div>
                  <div className="label-uppercase text-muted-foreground truncate">{active.email}</div>
                </div>
                <div className="text-xs label-uppercase text-muted-foreground hidden md:block">{active.role}</div>
              </header>

              <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto px-6 py-4 space-y-3 bg-background"
                data-testid="chat-messages"
              >
                {messages.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                    Inizia la conversazione con {formatName(active.full_name)}
                  </div>
                ) : (
                  messages.map((m) => {
                    const mine = m.from_id === user?.id;
                    return (
                      <div
                        key={m.id}
                        className={`flex ${mine ? "justify-end" : "justify-start"}`}
                        data-testid={`chat-msg-${m.id}`}
                      >
                        <div
                          className={`max-w-[75%] px-4 py-2 rounded-2xl text-sm leading-snug whitespace-pre-wrap break-words ${
                            mine
                              ? "bg-prime text-white rounded-br-sm"
                              : "bg-secondary text-foreground rounded-bl-sm border border-border"
                          }`}
                        >
                          <div>{m.body}</div>
                          <div className={`text-[10px] font-mono mt-1 ${mine ? "text-white/70" : "text-muted-foreground"} text-right`}>
                            {fmtTime(m.created_at)}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="border-t border-border p-4 flex items-end gap-2 bg-card">
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={onKey}
                  rows={2}
                  placeholder="Nachricht schreiben… (Enter zum Senden, Shift+Enter für neue Zeile)"
                  className="flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-prime/30"
                  data-testid="chat-input"
                />
                <Button
                  onClick={send}
                  disabled={!draft.trim() || sending}
                  className="bg-prime hover:bg-prime/90 text-white rounded-md gap-1 h-10 shrink-0"
                  data-testid="chat-send-btn"
                >
                  <Send className="w-4 h-4" strokeWidth={2} /> Senden
                </Button>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
